import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { cloudSave, cloudLoad } from "../lib/cloudStore";

/* ============================================================================
   SectionScanReport — Driver Routes › Section Scan

   Ingests Australia Post "scanner section" exports (the UTF-16 tab-separated
   `articles_id` CSVs — one driver per day) and shows, for each driver, WHICH
   SECTION the driver actually placed parcels into on the scanner device,
   day by day and for the week. This is the driver's *current* sectioning
   (ground truth), and it varies day to day with load and driver.

   Works for any driver / any area — the driver list and contract come straight
   from the files, nothing is hardcoded. Session-only; nothing writes back.

   Pure parsing/analysis lives up top so it can be tested outside React.
   ========================================================================== */

/* ---------- decode (files are UTF-16; fall back to UTF-8) ---------------- */
export function decodeScanBuffer(arrayBuffer) {
  const b = new Uint8Array(arrayBuffer);
  let enc = "utf-8";
  if (b[0] === 0xff && b[1] === 0xfe) enc = "utf-16le";
  else if (b[0] === 0xfe && b[1] === 0xff) enc = "utf-16be";
  return new TextDecoder(enc).decode(arrayBuffer).replace(/^﻿/, "");
}

const COL = {
  "WORK DATE": "date", "TIME": "time", "ARTICLE ID": "article",
  "ETA OUTCOME": "eta", "SECTION": "section",
  "SECTION START TIME": "secStart", "SECTION END TIME": "secEnd",
  "SUCCESS TIME": "success", "UNSUCCESS TIME": "unsuccess",
  "UNDEL TIME": "undel", "TRANSFER TIME": "transfer",
  "DRIVER AND CONTRACT": "driver",
};

/* parse one exported file's text into flat scan events */
export function parseSectionScanText(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.length);
  let hi = -1, header = null;
  for (let i = 0; i < Math.min(lines.length, 6); i++) {
    const cells = lines[i].split("\t").map((c) => c.trim());
    if (cells.some((c) => /^ARTICLE ID$/i.test(c))) { hi = i; header = cells; break; }
  }
  if (hi < 0) return [];
  const idx = {};
  header.forEach((c, i) => { const k = COL[c.toUpperCase()]; if (k) idx[k] = i; });
  const events = [];
  for (let i = hi + 1; i < lines.length; i++) {
    const r = lines[i].split("\t").map((c) => c.trim());
    const article = idx.article != null ? r[idx.article] : "";
    if (!article) continue; // section-marker rows have no article id
    const dc = (idx.driver != null ? r[idx.driver] : "").split(/\s+-\s+/);
    events.push({
      driver: (dc[0] || "").trim().toUpperCase(),
      contract: (dc[1] || "").trim(),
      date: idx.date != null ? r[idx.date] : "",
      time: idx.time != null ? r[idx.time] : "",
      section: (idx.section != null ? r[idx.section] : "") || "(unlabelled)",
      eta: idx.eta != null ? r[idx.eta] : "",
      secStart: idx.secStart != null ? r[idx.secStart] : "",
      secEnd: idx.secEnd != null ? r[idx.secEnd] : "",
      success: !!(idx.success != null && r[idx.success]),
      unsuccess: !!(idx.unsuccess != null && r[idx.unsuccess]),
      undel: !!(idx.undel != null && r[idx.undel]),
      transfer: !!(idx.transfer != null && r[idx.transfer]),
    });
  }
  return events;
}

/* ---------- time / date helpers ----------------------------------------- */
function tmin(t) {
  const m = /(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)/i.exec(t || "");
  if (!m) return 99999;
  let h = +m[1]; const mi = +m[2]; const pm = /pm/i.test(m[3]);
  if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12;
  return h * 60 + mi;
}
function hhmm(min) {
  if (min == null || min >= 99999) return "—";
  let h = Math.floor(min / 60); const m = min % 60;
  const ap = h < 12 ? "am" : "pm"; const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")}${ap}`;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function parseDMY(s) {
  const m = /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s || "");
  if (!m) return { key: s || "", label: s || "—", sort: 0 };
  const d = +m[1], mo = +m[2], y = +m[3] < 100 ? 2000 + +m[3] : +m[3];
  const dt = new Date(y, mo - 1, d);
  return { key: `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    label: `${WD[dt.getDay()]} ${String(d).padStart(2, "0")} ${MONTHS[mo - 1]}`,
    short: `${WD[dt.getDay()]} ${String(d).padStart(2, "0")}`, sort: dt.getTime() };
}

/* ---------- aggregate events into a per-driver report ------------------- */
export function buildScanReport(events) {
  const drivers = {};
  for (const e of events) {
    if (!e.driver) continue;
    const d = drivers[e.driver] || (drivers[e.driver] = {
      driver: e.driver, contract: e.contract || "", dates: {}, sections: {}, days: {},
    });
    const dm = parseDMY(e.date);
    if (!d.dates[dm.key]) d.dates[dm.key] = dm;
    const s = d.sections[e.section] || (d.sections[e.section] = { name: e.section, byDay: {}, total: 0, firstStart: e.secStart });
    s.byDay[dm.key] = (s.byDay[dm.key] || 0) + 1; s.total += 1;
    if (tmin(e.secStart) < tmin(s.firstStart)) s.firstStart = e.secStart;
    const day = d.days[dm.key] || (d.days[dm.key] = { total: 0, onTime: 0, predicted: 0, success: 0, unsuccess: 0, undel: 0, transfer: 0, sections: new Set(), times: [] });
    day.total += 1; day.sections.add(e.section);
    if (e.eta === "ONTIME") { day.onTime += 1; day.predicted += 1; }
    else if (e.eta && e.eta !== "NO_PREDICTION") day.predicted += 1;
    if (e.success) day.success += 1; if (e.unsuccess) day.unsuccess += 1;
    if (e.undel) day.undel += 1; if (e.transfer) day.transfer += 1;
    if (e.time) day.times.push(tmin(e.time));
  }
  for (const d of Object.values(drivers)) {
    d.dayKeys = Object.values(d.dates).sort((a, b) => a.sort - b.sort).map((x) => x.key);
    d.dateMeta = d.dates;
    d.sectionList = Object.values(d.sections).sort((a, b) => tmin(a.firstStart) - tmin(b.firstStart));
    let total = 0, onTime = 0, predicted = 0, success = 0, unsuccess = 0, undel = 0, transfer = 0;
    for (const k of d.dayKeys) {
      const day = d.days[k]; total += day.total; onTime += day.onTime; predicted += day.predicted;
      success += day.success; unsuccess += day.unsuccess; undel += day.undel; transfer += day.transfer;
      day.times.sort((a, b) => a - b);
      day.window = day.times.length ? `${hhmm(day.times[0])}–${hhmm(day.times[day.times.length - 1])}` : "—";
      day.onTimePct = day.predicted ? Math.round((day.onTime / day.predicted) * 100) : 100;
    }
    d.kpi = {
      parcels: total, days: d.dayKeys.length, avg: d.dayKeys.length ? Math.round(total / d.dayKeys.length) : 0,
      onTimePct: predicted ? Math.round((onTime / predicted) * 100) : 100,
      success, unsuccess, undel, transfer, sections: d.sectionList.length,
      successPct: total ? Math.round((success / total) * 100) : 0,
    };
  }
  const driverList = Object.keys(drivers).sort();
  return { drivers, driverList };
}

/* ---------- retain the loaded scan data across sessions (browser-local) -- */
const SCAN_KEY = "hp_sectionScan_v1";
function loadScan() {
  try { const s = localStorage.getItem(SCAN_KEY); return s ? JSON.parse(s) : null; } catch (e) { return null; }
}
function saveScan(events, driver) {
  if (!events || !events.length) { try { localStorage.removeItem(SCAN_KEY); } catch (e) {} return; }
  try { localStorage.setItem(SCAN_KEY, JSON.stringify({ events, driver })); } catch (e) {}
}

/* ======================================================================== */
export default function SectionScanReport({ login } = {}) {
  const [saved] = useState(loadScan);
  const [events, setEvents] = useState(() => (saved ? saved.events : null));
  const [driver, setDriver] = useState(() => (saved ? saved.driver || "" : ""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fileDrag, setFileDrag] = useState(false);
  const [cloud, setCloud] = useState(login ? "syncing" : "off");
  const cloudReady = useRef(false);
  const cloudTimer = useRef(null);

  const report = useMemo(() => (events && events.length ? buildScanReport(events) : null), [events]);

  // keep the selected driver valid as the data changes (also picks a default on restore)
  useEffect(() => {
    if (report && (!driver || !report.driverList.includes(driver))) {
      setDriver(report.driverList.includes("CHENZ19") ? "CHENZ19" : report.driverList[0] || "");
    }
  }, [report]);

  // Server-side retention (tied to the login): cloud is authoritative on mount;
  // if the cloud is empty but this browser has data, push it up. Then sync changes.
  useEffect(() => {
    if (!login) return;
    let cancelled = false;
    (async () => {
      const c = await cloudLoad(login, "sectionScan");
      if (cancelled) return;
      if (c && c.events && c.events.length) {
        setEvents(c.events);
        if (c.driver) setDriver(c.driver);
      } else if (saved && saved.events && saved.events.length) {
        cloudSave(login, "sectionScan", { events: saved.events, driver: saved.driver });
      }
      cloudReady.current = true;
      if (!cancelled) setCloud("synced");
    })();
    return () => { cancelled = true; };
  }, [login]); // eslint-disable-line

  // retain locally (instant) + debounced to the cloud (durable)
  useEffect(() => {
    saveScan(events, driver);
    if (login && cloudReady.current) {
      if (cloudTimer.current) clearTimeout(cloudTimer.current);
      cloudTimer.current = setTimeout(() => {
        cloudSave(login, "sectionScan", events && events.length ? { events, driver } : null);
      }, 1500);
    }
  }, [events, driver, login]);

  const clearSaved = () => { setEvents(null); setDriver(""); if (login) cloudSave(login, "sectionScan", null); };

  const onFiles = useCallback(async (fileList) => {
    const files = [...fileList].filter((f) => /\.(csv|txt|tsv)$/i.test(f.name));
    if (!files.length) { setErr("Drop the AP scanner section export(s) — the articles_id .csv files."); return; }
    setBusy(true); setErr("");
    try {
      const all = [];
      for (const f of files) {
        const text = decodeScanBuffer(await f.arrayBuffer());
        parseSectionScanText(text).forEach((e) => all.push(e));
      }
      if (!all.length) { setErr("No article scans found — is this the AP scanner section export?"); setBusy(false); return; }
      // Accumulate days/drivers across drops; a re-dropped driver-day refreshes just that day.
      const freshKeys = new Set(all.map((e) => e.driver + "|" + e.date));
      setEvents((prev) => [...(prev || []).filter((e) => !freshKeys.has(e.driver + "|" + e.date)), ...all]);
    } catch (e) { setErr("Couldn't read that file — expected the AP scanner section export."); }
    setBusy(false);
  }, []);

  const d = report && driver ? report.drivers[driver] : null;
  const maxCell = useMemo(() => {
    if (!d) return 1;
    let m = 1; d.sectionList.forEach((s) => d.dayKeys.forEach((k) => { if ((s.byDay[k] || 0) > m) m = s.byDay[k]; }));
    return m;
  }, [d]);

  const cellBg = (v) => (v ? `rgba(29,158,117,${0.10 + 0.55 * (v / maxCell)})` : "transparent");
  const colTotal = (k) => d.sectionList.reduce((a, s) => a + (s.byDay[k] || 0), 0);

  return (
    <div className={"ssr" + (fileDrag ? " filedrag" : "")}
      onDragOver={(e) => { if ([...(e.dataTransfer.types || [])].includes("Files")) { e.preventDefault(); if (!fileDrag) setFileDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setFileDrag(false); }}
      onDrop={(e) => { if (e.dataTransfer.files && e.dataTransfer.files.length) { e.preventDefault(); setFileDrag(false); onFiles(e.dataTransfer.files); } }}>
      <style>{CSS}</style>

      <div className="ssr-top">
        <div>
          <div className="ssr-eyebrow">Driver Routes · AP Scanner</div>
          <div className="ssr-title">Section Scan</div>
        </div>
        <div className="ssr-load">
          <label className="ssr-drop">
            <input type="file" accept=".csv,.txt,.tsv" multiple style={{ display: "none" }}
              onChange={(e) => onFiles(e.target.files)} />
            <span>{busy ? "Reading…" : report ? "Add / replace file(s)" : "Drop AP scanner .csv files"}</span>
          </label>
          {report && (
            <select className="ssr-sel" value={driver} onChange={(e) => setDriver(e.target.value)}>
              {report.driverList.map((dv) => <option key={dv} value={dv}>{dv}</option>)}
            </select>
          )}
          {report && (
            <span className="ssr-retain" title={login ? "Saved to your account — survives browser clears and follows you across devices. Drop new files to replace it." : "Saved in this browser. Drop new files to replace it."}>
              <span className="ssr-retain-dot" style={cloud === "syncing" ? { background: "#C8A24B" } : undefined} />
              {cloud === "syncing" ? "Syncing…" : (login ? "Saved" : "Retained")}
              <button className="ssr-clear" onClick={clearSaved}>Clear</button>
            </span>
          )}
        </div>
      </div>

      {err && <div className="ssr-err">{err}</div>}

      {!report && (
        <div className="ssr-empty">
          <div className="ssr-es-ic">⤓</div>
          <div className="ssr-es-t">Drop the AP scanner section exports to see the current sectioning</div>
          <div className="ssr-es-s">The <code>articles_id</code> .csv files — one per driver per day. Drop a whole week together. Every driver in the files is listed; parsed in your browser, nothing uploads.</div>
        </div>
      )}

      {d && (
        <>
          <div className="ssr-meta">
            <Cell k="Driver" v={d.driver} />
            <Cell k="Contract" v={d.contract || "—"} dim={!d.contract} />
            <Cell k="Days · Parcels/day" v={`${d.kpi.days} · ${d.kpi.avg}`} />
            <Cell k="Parcels (total)" v={String(d.kpi.parcels)} />
            <Cell k="On-time vs AP" v={`${d.kpi.onTimePct}%`} good={d.kpi.onTimePct >= 95} />
            <Cell k="Sections used" v={String(d.kpi.sections)} />
          </div>

          <div className="ssr-tbl-h">Current sectioning — which section {d.driver} places parcels into, by day</div>
          <div className="ssr-scroll">
            <table className="ssr-tbl">
              <thead>
                <tr>
                  <th className="lft">Section (scanner)</th>
                  {d.dayKeys.map((k) => <th key={k} className="num">{d.dateMeta[k].short}</th>)}
                  <th className="num wk">Week</th>
                </tr>
              </thead>
              <tbody>
                {d.sectionList.map((s) => (
                  <tr key={s.name}>
                    <td className="lft">{titleCase(s.name)}<span className="strt">{hhmm(tmin(s.firstStart))}</span></td>
                    {d.dayKeys.map((k) => {
                      const v = s.byDay[k] || 0;
                      return <td key={k} className="num" style={{ background: cellBg(v) }}>{v || <span className="dot">·</span>}</td>;
                    })}
                    <td className="num wk">{s.total}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="lft">Total parcels</td>
                  {d.dayKeys.map((k) => <td key={k} className="num">{colTotal(k)}</td>)}
                  <td className="num wk">{d.kpi.parcels}</td>
                </tr>
                <tr className="sub">
                  <td className="lft">On-time vs AP</td>
                  {d.dayKeys.map((k) => <td key={k} className="num">{d.days[k].onTimePct}%</td>)}
                  <td className="num wk">{d.kpi.onTimePct}%</td>
                </tr>
                <tr className="sub">
                  <td className="lft">Delivery window</td>
                  {d.dayKeys.map((k) => <td key={k} className="num win">{d.days[k].window}</td>)}
                  <td className="num wk">—</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="ssr-outc">
            <span className="ssr-oh">Week outcomes</span>
            <span className="ssr-chip ok">{d.kpi.success} delivered first scan ({d.kpi.successPct}%)</span>
            <span className="ssr-chip">{d.kpi.transfer} transferred</span>
            <span className="ssr-chip warn">{d.kpi.unsuccess} unsuccessful</span>
            <span className="ssr-chip warn">{d.kpi.undel} undeliverable</span>
          </div>

          <div className="ssr-foot">
            "Section" is the label {d.driver} placed each parcel under on the scanner — the current, real sectioning, which shifts day to day with load. Cell shading scales with parcel count. Drop more drivers' files to switch between them above. <span className="dim">Read-only — nothing writes back.</span>
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ k, v, dim, good }) {
  return (
    <div className="ssr-cell">
      <div className="k">{k}</div>
      <div className={"v" + (dim ? " dim" : "") + (good ? " good" : "")}>{v}</div>
    </div>
  );
}
function titleCase(s) {
  return (s || "").toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase()).replace(/\b(Oh|Mc|Av|Rd|St|Gdnd)\b/g, (m) => m.toUpperCase());
}

const CSS = `
.ssr{--ink:#16202B;--muted:#5C6875;--line:#E4E8ED;--bg:#F6F8FA;--teal:#1D9E75;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:var(--ink);
  background:var(--bg);padding:22px;border-radius:14px;max-width:1080px;}
.ssr *{box-sizing:border-box}
.ssr.filedrag{box-shadow:inset 0 0 0 2px #1B9C84;}
.ssr.filedrag .ssr-drop{background:#1B9C84;color:#fff;border-color:#1B9C84;}
.ssr-top{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;}
.ssr-eyebrow{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:var(--muted);font-weight:600;}
.ssr-title{font-size:23px;font-weight:700;margin:2px 0 0;letter-spacing:-.01em;}
.ssr-load{display:flex;gap:10px;align-items:center;}
.ssr-drop{font-size:12px;font-weight:700;color:#0C5A4C;background:#E3F4F0;border:1px solid #BFE6DD;
  padding:9px 15px;border-radius:9px;cursor:pointer;white-space:nowrap;}
.ssr-drop:hover{background:#D6EFE9;}
.ssr-sel{font-size:13px;font-weight:700;padding:8px 10px;border-radius:9px;border:1px solid var(--line);
  background:#fff;font-family:ui-monospace,monospace;cursor:pointer;}
.ssr-retain{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;color:#0C5A4C;}
.ssr-retain-dot{width:7px;height:7px;border-radius:50%;background:#1B9C84;display:inline-block;}
.ssr-clear{font-size:10.5px;font-weight:700;color:#8A610F;background:#FBF1DC;border:1px solid #ECD9A8;padding:3px 9px;border-radius:20px;cursor:pointer;font-family:inherit;}
.ssr-clear:hover{background:#F5E6BE;}
.ssr-err{margin-top:14px;font-size:12.5px;color:#9A3412;background:#FEF1E7;border:1px solid #F4CDB0;padding:10px 14px;border-radius:9px;}
.ssr-empty{margin-top:26px;border:1.5px dashed #CDD5DD;border-radius:14px;padding:46px 24px;text-align:center;background:#fff;}
.ssr-es-ic{font-size:34px;color:#9AA4AF;line-height:1;}
.ssr-es-t{font-size:16px;font-weight:700;margin-top:12px;}
.ssr-es-s{font-size:12.5px;color:var(--muted);margin-top:6px;max-width:480px;margin:6px auto 0;line-height:1.5;}
.ssr-es-s code{background:#EEF1F4;padding:1px 5px;border-radius:4px;font-size:11.5px;}
.ssr-meta{display:grid;grid-template-columns:repeat(6,1fr);gap:1px;background:var(--line);border:1px solid var(--line);
  border-radius:10px;overflow:hidden;margin:20px 0 22px;}
.ssr-cell{background:#fff;padding:11px 13px;}
.ssr-cell .k{font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);font-weight:600;}
.ssr-cell .v{font-size:16px;font-weight:700;margin-top:3px;font-variant-numeric:tabular-nums;}
.ssr-cell .v.dim{color:#9AA4AF;}.ssr-cell .v.good{color:var(--teal);}
.ssr-tbl-h{font-size:13px;font-weight:700;margin:0 2px 10px;}
.ssr-scroll{overflow-x:auto;border:1px solid var(--line);border-radius:12px;background:#fff;}
.ssr-tbl{border-collapse:collapse;width:100%;font-size:12.5px;}
.ssr-tbl th,.ssr-tbl td{padding:9px 12px;border-bottom:1px solid var(--line);white-space:nowrap;}
.ssr-tbl thead th{font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:700;background:#FBFCFD;position:sticky;top:0;}
.ssr-tbl .lft{text-align:left;font-weight:600;}
.ssr-tbl .num{text-align:right;font-family:ui-monospace,monospace;font-variant-numeric:tabular-nums;}
.ssr-tbl .wk{font-weight:800;border-left:1px solid var(--line);background:#FBFCFD;}
.ssr-tbl td.lft .strt{display:block;font-size:9.5px;color:#9AA4AF;font-weight:500;margin-top:1px;}
.ssr-tbl .dot{color:#CBD2D9;}
.ssr-tbl tfoot td{border-top:2px solid var(--line);border-bottom:none;font-weight:700;background:#FBFCFD;}
.ssr-tbl tfoot tr.sub td{font-weight:600;color:var(--muted);background:#fff;border-top:1px solid var(--line);}
.ssr-tbl tfoot .win{font-size:11px;}
.ssr-outc{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:16px 2px 0;}
.ssr-oh{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);}
.ssr-chip{font-size:11.5px;font-weight:700;padding:4px 11px;border-radius:20px;background:#EEF1F4;color:#4A5560;}
.ssr-chip.ok{background:#E3F4F0;color:#0C5A4C;}
.ssr-chip.warn{background:#FBF1DC;color:#8A610F;}
.ssr-foot{margin-top:18px;font-size:12px;color:var(--muted);line-height:1.55;}.ssr-foot .dim{color:#9AA4AF;}
@media (max-width:760px){.ssr-meta{grid-template-columns:repeat(2,1fr);}}
`;
